import React from "react";
import classnames from "classnames";
import { Button, Icon, Tooltip } from "@jelly/ui";
import { LessonCourseOverviewModal } from "@jelly/lesson";
import { Link } from "react-router-dom";

import useDashboardController, { DashboardCourse } from "../controllers/useDashboardController";
import Layout from "../components/Layout/Layout";
import ProgressBar from "../components/ProgressBar/ProgressBar";

import styles from "./Dashboard.module.scss";

const DashboardPage: React.FC = () => {
  const { courses, overviewModal, openOverviewModal, closeOverviewModal } = useDashboardController();

  if (!courses) {
    return null;
  }

  function renderCertificateIcon(url: string | null): React.ReactNode {
    if (url === null) {
      return <Button icon={"cis-badge"} shape={"round"} disabled />;
    }

    return (
      <Tooltip label="Download certificate" placement={"top"} theme="dark">
        <a href={url} download>
          <Button icon={"cis-badge"} shape={"round"} />
        </a>
      </Tooltip>
    );
  }

  function renderCourseListRow(course: DashboardCourse): React.ReactNode {
    return (
      <li key={course.courseId}>
        <div className={styles.iconWrapper} onClick={() => openOverviewModal(course.courseId)}>
          <Icon className={classnames("cis-list", styles.icon)} />
        </div>

        <div className={styles.progress}>
          <ProgressBar
            totalProgress={course.progress.totalProgress}
            correctAnswers={course.progress.correctAnswers}
            incorrectAnswers={course.progress.incorrectAnswers}
          />
        </div>
        <div className={styles.certificate}>{renderCertificateIcon(course.certificateUrl)}</div>
        <div className={styles.courseName}>
          <Link to={`/course/${course.courseId}`}>
            <Button size={"small"} icon={"cil-arrow-right"} iconPlacement={"right"} shape={"round"} theme={"secondary"}>
              {course.name}
            </Button>
          </Link>
        </div>
      </li>
    );
  }

  function renderCourseDetailsModal(): React.ReactNode {
    document.title = `ASKNow - Courses Dashboard`;

    if (!overviewModal) {
      // Always render modal for correct display opening animation
      return <LessonCourseOverviewModal course={{ id: "" }} lessons={[]} visible={false} />;
    }

    return (
      <LessonCourseOverviewModal
        course={{ id: overviewModal.course.id }}
        lessons={overviewModal.courseLessonsProgress}
        visible
        onClose={closeOverviewModal}
      >
        <div className={styles.courseDetailsModalHeader}>
          <h1>{overviewModal.course.name}</h1>
          <p>{overviewModal.course.description}</p>

          <ProgressBar
            totalProgress={overviewModal.courseProgress.progress.totalProgress}
            correctAnswers={overviewModal.courseProgress.progress.correctAnswers}
            incorrectAnswers={overviewModal.courseProgress.progress.incorrectAnswers}
          />
        </div>
      </LessonCourseOverviewModal>
    );
  }

  return (
    <Layout>
      <div className={styles.root}>
        <div className={styles.main}>
          <div className={styles.icon}>
            <Icon className={"cil-widgets"} theme={"primary"} />
          </div>

          <h1>Dashboard</h1>

          <p>
            Here you can find information about the progress of course completion. Tasks solved correctly are marked in
            green, and tasks you have solved incorrectly are marked in red. You can take the task multiple times until
            you get the correct answer.
          </p>
          <p>
            After exceeding 90% of lessons successfully completed, the option to download the Course Certificate will be
            unlocked.
          </p>
        </div>

        <div className={styles.details}>
          <h2>Course progress</h2>

          <ul className={styles.legendList}>
            <li className={styles.correct}>
              <div />
              Correct answers
            </li>
            <li className={styles.incorrect}>
              <div />
              Incorrect answers
            </li>
          </ul>

          <ul className={styles.coursesList}>{courses.map(renderCourseListRow)}</ul>
        </div>
      </div>

      {renderCourseDetailsModal()}
    </Layout>
  );
};

export default DashboardPage;
